// src/frontend/src/pages/SubscriptionPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AutobuyService } from '../AutobuyService';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Button,
    Card, CardContent, Divider, List, ListItem, 
    ListItemIcon, ListItemText, CircularProgress,
    Snackbar, Alert
} from '@mui/material';
import { 
    CheckCircle, WorkspacePremium as PremiumIcon,
    ShoppingCart
} from '@mui/icons-material';
import { format } from 'date-fns';
import { config } from '../Constants';

const URL = config.url;

const SubscriptionPage = () => {
    const { user, isAuthenticated, isPremium, login, authToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [, setCredits] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const location = useLocation();
    const navigate = useNavigate();

    const fetchUserCredits = async () => {
        try {
            const userCredits = await AutobuyService.getUserCredits();
            setCredits(userCredits);
        } catch (error) {
            console.error('Error fetching user credits:', error);
        }
    };

    const processCreditPurchaseSuccess = useCallback(async (sessionId) => {
        try {
            const newCredits = await AutobuyService.processCreditPurchase(sessionId);
            setCredits(newCredits);
            showSnackbar('Credits purchased successfully!', 'success');
            
            // Clear URL params
            navigate('/subscription', { replace: true });
        } catch (error) {
            console.error('Error processing credit purchase:', error);
            showSnackbar('Error processing purchase. Please contact support.', 'error');
        }
    }, [navigate]);

    useEffect(() => {
        // Check URL params for section selection
        const params = new URLSearchParams(location.search);
        
        // Check if coming back from successful credit purchase
        const sessionId = params.get('session_id');
        
        if (sessionId && location.pathname === '/credits/success') {
            processCreditPurchaseSuccess(sessionId);
        }
        
        // Handle redirect after login
        const savedRedirect = localStorage.getItem('redirectAfterLogin');
        if (savedRedirect) {
            localStorage.removeItem('redirectAfterLogin');
        }

        // Fetch user credits if authenticated
        if (isAuthenticated) {
            fetchUserCredits();
        }
    }, [isAuthenticated, location, processCreditPurchaseSuccess]);

    const handleUpgradeClick = async () => {
        if (!isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', '/subscription');
            login();
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/create-checkout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Redirect to Stripe checkout
                window.location.href = data.data.sessionUrl;
            } else {
                console.error('Error creating checkout session');
                showSnackbar('Error creating checkout session', 'error');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            showSnackbar('Error creating checkout session', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Premium member UI
    if (isPremium) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mb: 4 }}>
                    <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        You're a Premium Member!
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Thank you for supporting StockMaid. You have full access to all premium features.
                    </Typography>
                    {user?.premiumExpiry && (
                        <Typography variant="body2" color="text.secondary">
                            Your premium subscription is active until {format(new Date(user.premiumExpiry), 'MMMM d, yyyy')}
                        </Typography>
                    )}
                </Paper>

                <Typography variant="h5" gutterBottom>
                    Your Premium Benefits
                </Typography>

                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Detailed State Availability Map"
                            secondary="See exactly which states have your desired graphics card in stock"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Store Location Zip Codes"
                            secondary="Access exact zip codes to use during the Best Buy checkout process"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Monthly Autobuy Credit"
                            secondary="Receive 1 free autobuy credit each month to automatically reserve GPUs when they become available"
                        />
                    </ListItem>
                </List>
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to="/credits"
                        sx={{ mt: 2 }}
                    >
                        Manage Credits
                    </Button>
                </Box>
                
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        );
    }

    // Non-premium UI - simpler version with section selector
    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Typography variant="h4" align="center" gutterBottom>
                Upgrade to StockMaid Premium
            </Typography>
            <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6 }}>
                Get detailed state availability data, autobuy capability, and unlock all premium features
            </Typography>

            <Card elevation={3} sx={{ mb: 4, overflow: 'hidden' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                        <Typography variant="h5" fontWeight="bold">
                            Premium Membership
                        </Typography>
                    </Box>
                    
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" gutterBottom>
                            $9.99
                            <Typography component="span" variant="subtitle1" color="text.secondary">
                                /month
                            </Typography>
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={handleUpgradeClick}
                            disabled={loading}
                            sx={{ my: 2 }}
                            startIcon={<PremiumIcon />}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Subscribe Now'}
                        </Button>

                        <Typography variant="caption" color="text.secondary">
                            Cancel anytime. Secure payment via Stripe.
                        </Typography>
                    </Box>

                    <Divider />

                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText primary="Detailed State Availability Map" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText primary="Store Location Zip Codes" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircle color="success" />
                            </ListItemIcon>
                            <ListItemText primary="1 Monthly Autobuy Credit" secondary="Automatically reserve GPUs when they become available" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ShoppingCart color="success" />
                            </ListItemIcon>
                            <ListItemText primary="Purchase Additional Autobuy Credits" secondary="Available to premium members" />
                        </ListItem>
                    </List>
                </CardContent>
            </Card>

            <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.100' }}>
                <Typography variant="subtitle2" align="center">
                    Not ready to subscribe? You'll still have access to basic stock information.
                </Typography>
            </Paper>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default SubscriptionPage;